import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, type Schemas, unwrap } from "../api/client";
import { ErrorBox } from "../components/ErrorBox";
import { setSlot } from "../lib/identity";
import { CopyButton } from "../ws7/lib/ui";
import "../ws7/ws7.css";

export const JOIN_REDIRECT_MS = 4000;

/** Read `#c=<code>` from a join link (the code stays out of query strings and server logs). */
export function joinCode(hash: string): string | null {
  const m = /(?:^#|&)c=([^&]+)/.exec(hash);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

function HumanJoined({ j, redirectMs }: { j: Schemas["JoinOut"]; redirectMs: number }) {
  const nav = useNavigate();
  useEffect(() => {
    const t = window.setTimeout(() => nav(j.home, { replace: true }), redirectMs);
    return () => window.clearTimeout(t);
  }, [j.home, nav, redirectMs]);
  return (
    <section aria-labelledby="join-h" data-testid="join-human">
      <h2 id="join-h" data-testid="join-who">You are {j.actor} ({j.role_title})</h2>
      <p>
        This tab is now logged in as <strong>{j.actor}</strong>. Other people logged in in this browser stay logged in in
        their own tabs; switch with the name chip at the top right.
      </p>
      <p>
        Read your brief first: <Link to={j.brief_url} data-testid="join-brief-link">{j.role_title} brief</Link>
      </p>
      <p>
        Taking you to your start page in a few seconds…{" "}
        <Link to={j.home} replace className="button" data-testid="join-continue">Go to {j.home} now</Link>
      </p>
    </section>
  );
}

function BotJoined({ j }: { j: Schemas["JoinOut"] }) {
  const bundle = j.connect ?? "";
  return (
    <section aria-labelledby="join-h" data-testid="join-bot">
      <h2 id="join-h" data-testid="join-who">Bot {j.actor} ({j.role_title}) is ready</h2>
      <div role="alert" className="ws7-secret" data-testid="join-once">
        <strong>Shown once.</strong> Copy this connect bundle now and keep VP_TOKEN secret. It cannot be shown again; an
        admin can revoke it and send a new join link.
      </div>
      <pre data-testid="join-bundle" aria-label="Connect bundle">{bundle}</pre>
      <p>
        <CopyButton text={bundle} label="Copy connect bundle" testId="join-copy" what="the connect bundle" />{" "}
        <Link to={j.brief_url} data-testid="join-brief-link">{j.role_title} brief</Link>
      </p>
    </section>
  );
}

/** /join#c=<code>: redeem a one-time join link. A person gets a session in a new identity slot of this browser
 * (this tab is bound to it); a bot gets its token and a connect bundle, shown once. */
export function JoinPage({ redirectMs = JOIN_REDIRECT_MS }: { redirectMs?: number } = {}) {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [joined, setJoined] = useState<Schemas["JoinOut"] | null>(null);
  const [missing, setMissing] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // a join code works once: StrictMode must not redeem it twice
    started.current = true;
    const code = joinCode(window.location.hash);
    window.history.replaceState(null, "", "/join");
    if (!code) {
      setMissing(true);
      return;
    }
    void (async () => {
      try {
        const j = await unwrap(api.POST("/api/auth/join", { body: { code } }));
        if (j.slot) {
          setSlot(j.slot);
          await qc.resetQueries();
        }
        setJoined(j);
      } catch (e) {
        setError(e);
      }
    })();
  }, [qc]);

  return (
    <main className="login join" data-testid="join-page">
      <h1>Join the Vezérlőpult</h1>
      <ErrorBox error={error} />
      {missing && <p data-testid="join-missing">This join link has no code. Ask an admin for a new link.</p>}
      {!joined && !error && !missing && <p data-testid="join-pending">Joining…</p>}
      {joined && (joined.kind === "human" ? <HumanJoined j={joined} redirectMs={redirectMs} /> : <BotJoined j={joined} />)}
    </main>
  );
}
